import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type YearProps = {
  year: number;
  startDate: DateTime;
  endDate: DateTime;
};

export type DatesOptions = {
  text: string;
  range?: {
    start: DateTime;
    end: DateTime;
  };
  id: string;
  withRange?: boolean;
};

export type FilterState = {
  attendance?: DatesOptions;
  course?: string;
  tee?: string;
  option?: string;
};

export type USGACourse = {
  courseId: string;
  courseName: string;
  tees: USGATee[];
};

export type USGATee = {
  teeSetId: string;
  teeName: string;
};

type FilterOptions =
  | 'recent_and_revision_scores'
  | 'scores_by_course_or_tee'
  | 'past_12month_scores'
  | 'competition_scores'
  | string; // compatible type for ${number}_scores

export type StatsFilterOption = {
  title: string;
  value: FilterOptions;
};

export const useGetPastYears = (length: number): YearProps[] => {
  return useMemo(() => {
    const startOfYear = DateTime.now().startOf('year');
    const currentYear = startOfYear.get('year');

    return Array.from({ length }, (_, index) => {
      const year = currentYear - index;

      return {
        year,
        startDate: startOfYear.set({ year }),
        endDate: startOfYear.set({ year }).endOf('year'),
      };
    });
  }, [length]);
};

export const useGetAttendanceOptions = () => {
  const pastYears = useGetPastYears(4);
  const { t } = useTranslation();
  const now = DateTime.now();

  return useMemo(() => {
    const options: DatesOptions[] = [
      {
        text: t('Recent Scores'),
        id: '1',
      },
      ...pastYears.map(date => ({
        text: t('{{year}} Scores', {
          year: date.year,
        }),
        id: date.year.toString(),
        range: {
          start: date.startDate,
          end: date.endDate,
        },
      })),
      {
        text: t('Custom Range'),
        id: '6',
        range: {
          start: now.startOf('month'),
          end: now.endOf('month'),
        },
        withRange: true,
      },
    ];

    return options;
  }, [pastYears, now]);
};

export const useGetSelectOptions = () => {
  const pastYears = useGetPastYears(4);
  const { t } = useTranslation();

  const yearOptions: StatsFilterOption[] = pastYears.map(year => ({
    value: `${year.year}_scores`,
    title: t('{{year}} Scores', { year: year.year }).toString(),
  }));

  const recentOption: StatsFilterOption = {
    title: t('Recent & Revision Scores'),
    value: 'recent_and_revision_scores',
  };

  const lastOptions: StatsFilterOption[] = [
    {title: t('Course / Tee'), value: 'scores_by_course_or_tee'},
    {title: t('Past 12 Months'), value: 'past_12month_scores'},
    {title: t('Competition Scores'), value: 'competition_scores'},
  ];

  return [recentOption, ...yearOptions, ...lastOptions];
};
